import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Switch } from '@repo/design-system';
import { glass, radius, semantic, shadow, space, typeRole } from '@repo/design-tokens';
import { CameraPictureModal, visuallyHidden, type CameraWallFeed } from '@repo/ui';
import { useAuth0 } from '@auth0/auth0-react';
import type { HeatGridWindow } from '@repo/frontend-types';

import {
  HEAT_GRID_CELL_PX,
  useHeatGridImage,
} from '../../components/PolygonPlotter2/heatGridImage';
import { useCameraHeat } from './useCameraHeat';
import { windowPhrase } from './heatCaption';
import { useHeatSettings } from './heatSettings';
import { useImageNaturalSize } from './useImageNaturalSize';

/** What each heat state says, beside the switch and in its live region (criterion 10). */
export const CAMERA_HEAT_TEXT = {
  loading: 'Loading heat…',
  /** Follows the remembered window (`onboarding-heatmap-remembered`, R9). */
  empty: (heatWindow: HeatGridWindow): string =>
    heatWindow === '1d' ? 'No heat yesterday' : `No heat in the ${windowPhrase(heatWindow)}`,
  failed: 'Couldn’t load the heat',
  loaded: 'Heat loaded',
} as const;

export interface CameraHeatPicture {
  /** Drawn over the picture (`CameraPictureModal`'s `overlay`): the heat image while it is on. */
  overlay: React.ReactNode;
  /** The top-right controls (`CameraPictureModal`'s `controls`): the switch, its caption, Retry. */
  controls: React.ReactNode;
}

/**
 * The full-screen picture's people heat and its Heatmap switch (`unified-camera-panel`, R5.5-R5.6).
 * Used by `CameraFullScreenPicture`, which mounts with the open modal and goes with it, so the live
 * region is empty every time the picture opens. The switch is the person's remembered Heatmap
 * choice, the same one as the Observers step's (`onboarding-heatmap-remembered`, R3): it opens as
 * they left it, and turning it here turns it there. It draws the Observers step's whole-body heat
 * over the remembered window (R8) over the picture, and off hides it.
 *
 * - **Loading:** the switch as left, "Loading heat…" beside it, the picture without heat.
 * - **Empty:** the switch disabled, "No heat in the last {n} days" (or "No heat yesterday").
 *   A disabled switch never changes the stored choice (R10).
 * - **Failed:** the switch disabled, "Couldn't load the heat" and Retry. Cells that decode but paint
 *   nothing are failed too, as on Observers, and logged. A stream with no BID to read heat by is
 *   failed without Retry: nothing can be read for it.
 * The picture shows whatever the heat does, and every state is said in words, not by colour.
 */
export const useCameraHeatPicture = ({
  rtspBid,
  imageUrl,
}: {
  rtspBid: string | null;
  imageUrl: string | undefined;
}): CameraHeatPicture => {
  const { user } = useAuth0();
  const { heatOn: on, setHeatOn: setOn, window: heatWindow } = useHeatSettings(user?.sub);
  const heat = useCameraHeat(rtspBid, heatWindow);
  const emptyText = CAMERA_HEAT_TEXT.empty(heatWindow);
  const natural = useImageNaturalSize(imageUrl);
  const image = useHeatGridImage(
    heat.state === 'ready' ? heat.cells : null,
    natural ? { w: natural.width, h: natural.height } : null,
  );
  const paintFailed = heat.state === 'ready' && !!natural && !image;
  const noBid = !rtspBid;
  const failed = heat.state === 'failed' || paintFailed || noBid;
  const disabled = heat.state === 'empty' || failed;

  useEffect(() => {
    if (paintFailed) {
      console.warn('[camera-heat] heat decoded but painted nothing for stream', rtspBid, natural);
    }
    // Once per entry into the state, not per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paintFailed]);

  const caption = failed
    ? CAMERA_HEAT_TEXT.failed
    : heat.state === 'empty'
      ? emptyText
      : heat.state === 'ready'
        ? null
        : CAMERA_HEAT_TEXT.loading;

  // The live region says each settled state once, and the switch's change.
  const [said, setSaid] = useState('');
  const settledText = failed
    ? CAMERA_HEAT_TEXT.failed
    : heat.state === 'empty'
      ? emptyText
      : // "Heat loaded" only while the heat is shown: a picture that opens with the remembered
        // switch off says nothing until the switch is turned on (its change is said).
        heat.state === 'ready' && image && on
        ? CAMERA_HEAT_TEXT.loaded
        : null;
  const lastSettled = useRef<string | null>(null);
  useEffect(() => {
    if (settledText && settledText !== lastSettled.current) setSaid(settledText);
    lastSettled.current = settledText;
  }, [settledText]);

  /*
   * A control that goes away or is disabled under focus would drop focus out of the dialog: Retry
   * leaves as the read starts again, and the switch disables when a read settles empty or failed.
   * Focus moves to the switch after Retry, and to Close when the focused switch disables. Some
   * browsers blur an input as it disables, before this effect runs, so whether the switch held focus
   * is recorded as it happens: a blur to no element keeps the record (review).
   */
  const switchRef = useRef<HTMLInputElement | null>(null);
  const focusSwitch = useRef(false);
  const switchHadFocus = useRef(false);
  useEffect(() => {
    const sw = switchRef.current;
    if (!sw) return;
    const active = document.activeElement;
    const lostToNowhere = switchHadFocus.current && (active === null || active === document.body);
    if (focusSwitch.current && !disabled) {
      focusSwitch.current = false;
      sw.focus();
    } else if (disabled && (active === sw || lostToNowhere)) {
      switchHadFocus.current = false;
      sw
        .closest('[role="dialog"]')
        ?.querySelector<HTMLButtonElement>('button[aria-label="Close"]')
        ?.focus();
    }
  }, [disabled]);

  const overlay =
    on && image && natural ? (
      // The picture's own coordinates, fitted as the picture is (`contain`), so the heat sits on the
      // people whatever ratio the stream declared: `FrameRegionOverlay`'s fitting.
      <HeatLayer
        data-camera-heat-image=""
        aria-hidden="true"
        viewBox={`0 0 ${natural.width} ${natural.height}`}
        preserveAspectRatio="xMidYMid meet"
        overflow="hidden"
      >
        <image
          href={image.url}
          x={0}
          y={0}
          width={image.width * HEAT_GRID_CELL_PX}
          height={image.height * HEAT_GRID_CELL_PX}
          preserveAspectRatio="none"
        />
      </HeatLayer>
    ) : null;

  const controls = (
    <Controls data-camera-heat-controls="">
      <Switch
        ref={switchRef}
        surface="on-glass"
        checked={on && !disabled}
        disabled={disabled}
        onFocus={() => {
          switchHadFocus.current = true;
        }}
        onBlur={e => {
          // Focus going to an element ends the record; a blur to nowhere (none, or the document)
          // may be the disabling.
          if (e.relatedTarget instanceof Element) switchHadFocus.current = false;
        }}
        onChange={e => {
          setOn(e.currentTarget.checked);
          setSaid(e.currentTarget.checked ? 'Heatmap on' : 'Heatmap off');
        }}
      >
        Heatmap
      </Switch>
      {caption ? <Caption>{caption}</Caption> : null}
      {failed && !noBid ? (
        <Button
          variant="ghost"
          size="xs"
          aria-label="Retry loading heat"
          onClick={() => {
            // The same outcome again is said again.
            lastSettled.current = null;
            setSaid('');
            focusSwitch.current = true;
            heat.retry();
          }}
        >
          Retry
        </Button>
      ) : null}
      <span role="status" style={visuallyHidden}>
        {said}
      </span>
    </Controls>
  );

  return { overlay, controls };
};

export interface CameraFullScreenPictureProps {
  feed: CameraWallFeed;
  /** The camera's name: the dialog is "{name}, full screen picture". */
  displayName: string;
  /** The stream's BID, for its heat; null when the twin's list and the camera name none. */
  rtspBid: string | null;
  /** The picture it grows from. */
  anchor: HTMLElement | null;
  /** Where focus returns on close. */
  returnFocusTo: HTMLElement | null;
  onClose: () => void;
}

/**
 * The camera panel's full-screen picture (R5), with its heat. Mounted only while open, so each open
 * reads the heat afresh; the switch opens as the person left it (`onboarding-heatmap-remembered`).
 */
export const CameraFullScreenPicture: React.FC<CameraFullScreenPictureProps> = ({
  feed,
  displayName,
  rtspBid,
  anchor,
  returnFocusTo,
  onClose,
}) => {
  const heat = useCameraHeatPicture({ rtspBid, imageUrl: feed.imageUrl });
  return (
    <CameraPictureModal
      feed={feed}
      label={`${displayName}, full screen picture`}
      anchor={anchor}
      returnFocusTo={returnFocusTo}
      onClose={onClose}
      overlay={heat.overlay}
      controls={heat.controls}
    />
  );
};

/* Over the whole picture box; the viewBox fits the picture's own coordinates inside it. */
const HeatLayer = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
`;

/* The switch, its words and Retry, on a frosted pill at the picture's top right. */
const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[1]} ${space[3]};
  border-radius: ${radius.pill};
  background: ${glass.bgStrong};
  backdrop-filter: ${glass.blurSm};
  -webkit-backdrop-filter: ${glass.blurSm};
  box-shadow: ${shadow.glassSm};
`;

const Caption = styled.span`
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.secondary};
  white-space: nowrap;
`;
